'use client'

import { useEffect, useState } from 'react'
import { useLicenceControlStore } from '@repo/store/useLicenceControlStore'
import MUIButton from '@mui/material/Button'
import MUICircularProgress from '@mui/material/CircularProgress'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIStack from '@mui/material/Stack'
import MUISwitch from '@mui/material/Switch'
import MUITextField from '@mui/material/TextField'
import MUITypography from '@mui/material/Typography'

export const LicenceControlSettings = () => {
  const { fetchLicenceControl, updateLicenceControl } = useLicenceControlStore()
  const [maxConcurrentUsers, setMaxConcurrentUsers] = useState<number | null>(null)
  const [enforcementEnabled, setEnforcementEnabled] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await fetchLicenceControl()
        setMaxConcurrentUsers(result?.maxConcurrentUsers ?? null)
        setEnforcementEnabled(result?.enforcementEnabled ?? false)
      } catch (error) {
        console.error('Error fetching licence control settings:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleMaxConcurrentUsersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setMaxConcurrentUsers(value === '' ? null : Number(value))
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await updateLicenceControl({ maxConcurrentUsers, enforcementEnabled })
    } catch (error) {
      console.error('Error saving licence control settings:', error)
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <MUIStack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
        <MUICircularProgress />
      </MUIStack>
    )
  }

  return (
    <MUIStack data-testid="LicenceControlSettings" direction="column" gap={4}>
      <MUITypography variant="body2" sx={{ mt: -1, mb: 2 }}>
        <em>Set the contracted concurrent-user limit for this site and whether it is enforced.</em>
      </MUITypography>

      <MUITextField
        label="Maximum concurrent users"
        variant="outlined"
        type="number"
        value={maxConcurrentUsers ?? ''}
        onChange={handleMaxConcurrentUsersChange}
      />

      <MUIFormControlLabel
        control={
          <MUISwitch
            checked={enforcementEnabled}
            onChange={(e) => setEnforcementEnabled(e.target.checked)}
            inputProps={{ 'aria-label': 'Enforce the concurrent-user limit' }}
          />
        }
        label="Enforce the concurrent-user limit"
      />

      <MUIButton
        data-testid="LicenceControlSaveButton"
        variant="contained"
        color="primary"
        size="large"
        onClick={handleSave}
        disabled={isSaving}
        sx={{ maxWidth: 160, width: '100%', margin: '0 0 0 auto' }}
      >
        {isSaving ? <MUICircularProgress size={24} color="inherit" /> : 'Save'}
      </MUIButton>
    </MUIStack>
  )
}
